import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Instagram, MessageCircle, Linkedin, Film, ShoppingBag, Copy, Check, Wand2, ArrowRight } from "lucide-react";
import { useHOH, STEP } from "../../../context/StyleContext";
import StepFrame, { StepTitle } from "../StepFrame";
import { Textarea } from "../../ui/textarea";
import { toast } from "sonner";

const FIELDS = [
  { key: "instagramCaption", label: "Instagram Caption", icon: Instagram },
  { key: "whatsappStatus", label: "WhatsApp Status", icon: MessageCircle },
  { key: "linkedinPost", label: "LinkedIn Post", icon: Linkedin },
  { key: "reelScript", label: "Reel Script", icon: Film },
  { key: "getTheLookText", label: "Get The Look", icon: ShoppingBag },
];

export default function ContentStudioStep() {
  const { content, generatingContent, runGenerateContent, goTo } = useHOH();
  const [draft, setDraft] = useState({});
  const [copied, setCopied] = useState(null);

  useEffect(() => { if (!content && !generatingContent) runGenerateContent(); }, []);
  useEffect(() => { if (content) setDraft(content); }, [content]);

  const copy = (k) => { navigator.clipboard?.writeText(draft[k] || ""); setCopied(k); toast.success("Copied"); setTimeout(() => setCopied(null), 1400); };

  return (
    <StepFrame wide className="text-center">
      <StepTitle overline="Step 8" title="Your look, ready to share" subtitle="AI-crafted captions and scripts from your final look." />

      {generatingContent || !content ? (
        <div className="py-16 flex flex-col items-center"><div className="w-16 h-16 rounded-full bg-hoh-rose/15 flex items-center justify-center animate-pulse"><Wand2 className="w-7 h-7 text-hoh-rose" /></div><p className="mt-4 text-hoh-slate">Writing your content…</p></div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-5 text-left">
            {FIELDS.map((f, i) => (
              <motion.div key={f.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }} className="hoh-card p-5" data-testid={`content-${f.key}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2"><div className="w-8 h-8 rounded-xl bg-hoh-blush flex items-center justify-center"><f.icon className="w-4 h-4 text-hoh-rose" /></div><span className="font-semibold text-black text-sm">{f.label}</span></div>
                  <button onClick={() => copy(f.key)} className="w-8 h-8 rounded-full border border-black/10 flex items-center justify-center hover:bg-hoh-blush" data-testid={`copy-${f.key}`}>{copied === f.key ? <Check className="w-4 h-4 text-hoh-sage" /> : <Copy className="w-4 h-4 text-hoh-navy" />}</button>
                </div>
                <Textarea value={draft[f.key] || ""} onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })} className="rounded-2xl resize-none min-h-[70px] text-hoh-navy" data-testid={`textarea-${f.key}`} />
              </motion.div>
            ))}
          </div>
          {draft.hashtags?.length > 0 && (
            <div className="hoh-card p-5 mt-5 text-left"><span className="font-semibold text-black text-sm">Hashtags</span><div className="mt-3 flex flex-wrap gap-2">{draft.hashtags.map((h) => <span key={h} className="bg-hoh-cream rounded-full px-3 py-1 text-sm text-hoh-rose font-medium">{h}</span>)}</div></div>
          )}
          <button onClick={() => goTo(STEP.TRENDING)} className="btn-royal mt-10" data-testid="publish-trending-btn">Publish To Trending <ArrowRight className="w-4 h-4" /></button>
        </>
      )}
    </StepFrame>
  );
}
